// LenInput.jsx — Phase 3
//
// A unit-aware <input type="number"> for lengths. The canonical value is ALWAYS
// millimetres (mm): `mm` in, `onMm(mm)` out. Display/entry happens in the active
// unit (mm or in) from the theme. A local string buffer keeps typing smooth —
// intermediate states like "0." or "1." aren't clobbered by a parse→format
// round-trip — while external changes (presets) and unit toggles resync cleanly.

import React from 'react';
import { useTheme } from './theme';

export default function LenInput({ mm, onMm, style, min = 0, stepMm = 1, title, placeholder }) {
  const { unit, toDisplay, fromDisplay } = useTheme();
  const [str, setStr] = React.useState(() => String(toDisplay(mm)));

  // Unit changed → the shown number must change representation. Always resync.
  React.useEffect(() => {
    setStr(String(toDisplay(mm)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);

  // External mm change (e.g. a preset) → resync, but never fight the user's own
  // keystrokes (when the buffer already represents this mm value).
  React.useEffect(() => {
    const localMm = fromDisplay(parseFloat(str));
    if (Number.isNaN(localMm) || Math.abs((localMm || 0) - (mm || 0)) > 1e-6) {
      setStr(String(toDisplay(mm)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mm]);

  const step = unit === 'in' ? Math.max(0.01, Math.round((stepMm / 25.4) * 1000) / 1000) : stepMm;

  return (
    <input
      type="number"
      min={min}
      step={step}
      value={str}
      title={title}
      placeholder={placeholder}
      style={style}
      onChange={(e) => {
        const raw = e.target.value;
        setStr(raw);
        if (raw === '') { onMm(0); return; }
        const v = parseFloat(raw);
        if (!Number.isNaN(v)) onMm(fromDisplay(v));
      }}
    />
  );
}
